import { type ComponentType } from 'react';

import { action, autorun, makeObservable, observable } from 'mobx';

import soundEffects, { type SoundEffect } from '../../services/sounds/soundEffects';
import { type RootStore } from '../RootStore';

export type PortalOptions = Partial<{
  persistent: boolean;
  contentClassName: string;
  beforeClose: VoidFunction;
  openSoundEffect: SoundEffect;
  closeSoundEffect: SoundEffect;
  silent: boolean;
  type: 'dailyBonus';
  [key: string]: unknown;
}>;

export type PortalState = 'opening' | 'opened' | 'closing' | 'closed';

export class PortalStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    autorun(() => {
      if (this.rootStore && !this.rootStore.authStore.authorized) {
        this.closePortal();
      }
    });
  }

  @observable
  public isVisible: boolean = false;

  private _portalState: PortalState = 'closed';

  public get portalState() {
    return this._portalState;
  }

  private set portalState(portalState) {
    this._portalState = portalState;
  }

  @observable
  public isRefreshing: boolean = false;

  private _Component: ComponentType | null = null;

  public get Component() {
    return this._Component;
  }

  private set Component(Component) {
    this._Component = Component;
  }

  private _options: PortalOptions = {};

  public get options() {
    return this._options;
  }

  private set options(options) {
    this._options = options;
  }

  @action
  private setVisible(isVisible: boolean) {
    this.isVisible = isVisible;

    if (isVisible) {
      this.portalState = this.portalState === 'opened' ? 'opened' : 'opening';
    } else {
      this.portalState = this.portalState === 'closed' ? 'closed' : 'closing';
    }
  }

  @action
  public setRefreshing(isRefreshing: boolean) {
    this.isRefreshing = isRefreshing;
  }

  public closePortal = (args?: { preventBeforeClose: boolean }) => {
    if (this.options.type === 'dailyBonus') {
      this.rootStore.rewardsStore.setDailyBonusCompleted();
    }

    if (typeof this.options.beforeClose === 'function' && !args?.preventBeforeClose) {
      this.options.beforeClose();
    }
    this.setVisible(false);

    if (!this.options.silent) {
      soundEffects.playSound(this.options.closeSoundEffect || 'popup-close');
    }
  };

  public fullyClosePortal() {
    this.portalState = 'closed';

    if (this.isRefreshing) {
      this.setRefreshing(false);
      this.setVisible(true);
    } else {
      this.Component = null;
      this.options = {};
    }
  }

  public openPortal(Component: ComponentType | null = null, options: PortalOptions = {}) {
    if (this.isVisible) {
      this.setRefreshing(true);
      this.setVisible(false);
    } else {
      this.setVisible(true);
    }

    this.Component = Component;
    this.options = options;
    if (!this.options.silent) {
      soundEffects.playSound(this.options.openSoundEffect || 'popup-open');
    }
  }

  public fullyOpenPortal() {
    this.portalState = 'opened';
  }
}
